import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { useApi } from '../hooks/useApi.js';
import { Bell } from 'lucide-react';

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const api = useApi();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000); // poll every 30s
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function fetchNotifications() {
    try {
      const data = await api.get('/notifications');
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch {}
  }

  async function handleMarkAllRead() {
    try {
      await api.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  }

  async function handleMarkRead(id) {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  }

  function handleLogout() {
    logout();
    setMobileOpen(false);
    navigate('/login');
  }

  const isOwner = user?.role === 'owner';

  return (
    <>
    <nav className="navbar">
      <NavLink to="/dashboard" className="navbar-brand">
        {user?.logo_url ? (
          <img src={user.logo_url} alt="logo" style={{ height: 32, width: 32, objectFit: 'cover', borderRadius: 4, marginRight: 8 }} />
        ) : null}
        Roster<span>AI</span>
      </NavLink>

      <div className="navbar-links">
        <NavLink to="/dashboard" className={({ isActive }) => isActive ? 'active' : ''}>
          Dashboard
        </NavLink>
        {isOwner && (
          <NavLink to="/employees" className={({ isActive }) => isActive ? 'active' : ''}>
            Employees
          </NavLink>
        )}
        <NavLink to="/roster" className={({ isActive }) => isActive ? 'active' : ''}>
          Roster
        </NavLink>
        <NavLink to="/availability" className={({ isActive }) => isActive ? 'active' : ''}>
          Availability
        </NavLink>
        <NavLink to="/open-shifts" className={({ isActive }) => isActive ? 'active' : ''}>
          Open Shifts
        </NavLink>
        {isOwner ? (
          <>
            <NavLink to="/settings" className={({ isActive }) => isActive ? 'active' : ''}>
              Settings
            </NavLink>
            <NavLink to="/audit" className={({ isActive }) => isActive ? 'active' : ''}>
              Audit Log
            </NavLink>
          </>
        ) : (
          <NavLink to="/account" className={({ isActive }) => isActive ? 'active' : ''}>
            Account
          </NavLink>
        )}
        <NavLink to="/help" className={({ isActive }) => isActive ? 'active' : ''}>
          Help
        </NavLink>
      </div>

      <div className="navbar-right">
        {/* Notification Bell */}
        <div ref={dropdownRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setOpen(prev => !prev)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', position: 'relative', padding: '4px 8px', color: '#fff' }}
            title="Notifications"
          >
            <Bell size={20} />
            {unreadCount > 0 && (
              <span style={{
                position: 'absolute', top: 0, right: 0,
                background: '#ef4444', color: '#fff',
                borderRadius: '50%', fontSize: 10, fontWeight: 700,
                minWidth: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '0 3px',
              }}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {open && (
            <div style={{
              position: 'fixed', right: 8, top: 'calc(var(--nav-height) + 4px)',
              width: 'min(320px, calc(100vw - 16px))',
              background: 'var(--bg-card, #fff)', border: '1px solid var(--border)',
              borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.12)', zIndex: 1000,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>Notifications</span>
                {unreadCount > 0 && (
                  <button onClick={handleMarkAllRead} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', fontSize: 12 }}>
                    Mark all read
                  </button>
                )}
              </div>
              <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                {notifications.length === 0 ? (
                  <div style={{ padding: '20px 14px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                    No notifications
                  </div>
                ) : (
                  notifications.map(n => (
                    <div
                      key={n.id}
                      onClick={() => {
                        if (!n.read) handleMarkRead(n.id);
                        if (['shift_opened', 'shift_assigned', 'shift_updated', 'request_approved', 'request_rejected', 'shift_requested'].includes(n.type)) {
                          setOpen(false);
                          navigate('/open-shifts');
                        }
                      }}
                      style={{
                        padding: '10px 14px',
                        borderBottom: '1px solid var(--border)',
                        background: n.read ? 'transparent' : 'var(--bg-subtle, #f0f7ff)',
                        cursor: 'pointer',
                        fontSize: 13,
                      }}
                    >
                      <div style={{ marginBottom: 4, color: 'var(--text)', fontWeight: 500 }}>
                        {n.actor_name && n.message.includes(n.actor_name)
                          ? (() => {
                              const parts = n.message.split(n.actor_name);
                              return (
                                <>
                                  {parts[0]}
                                  <span style={{ color: 'var(--color-primary)', fontWeight: 700 }}>{n.actor_name}</span>
                                  {parts.slice(1).join(n.actor_name)}
                                </>
                              );
                            })()
                          : n.message}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <strong style={{ color: 'var(--text-muted)', fontWeight: 700 }}>
                          {new Date(n.created_at).toLocaleDateString('en-AU', { dateStyle: 'short' })}
                        </strong>
                        <span>{new Date(n.created_at).toLocaleTimeString('en-AU', { timeStyle: 'short' })}</span>
                        {!n.read && <span style={{ marginLeft: 2, color: 'var(--color-primary)', fontWeight: 700 }}>• New</span>}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <span className="navbar-user">
          <strong>{user?.name}</strong>
          {user?.role === 'owner' ? ' (Owner)' : ` (${user?.employeeRole || 'Staff'})`}
        </span>
        <button className="btn btn-secondary btn-sm" onClick={handleLogout}>
          Log out
        </button>
        <button
          className="navbar-hamburger"
          onClick={() => setMobileOpen(prev => !prev)}
          aria-label="Menu"
        >
          {mobileOpen ? '✕' : '☰'}
        </button>
      </div>
    </nav>

    {/* Mobile slide-down menu */}
    <div className={`navbar-mobile-menu${mobileOpen ? ' open' : ''}`}>
      <NavLink to="/dashboard" onClick={() => setMobileOpen(false)} className={({ isActive }) => isActive ? 'active' : ''}>Dashboard</NavLink>
      {isOwner && <NavLink to="/employees" onClick={() => setMobileOpen(false)} className={({ isActive }) => isActive ? 'active' : ''}>Employees</NavLink>}
      <NavLink to="/roster" onClick={() => setMobileOpen(false)} className={({ isActive }) => isActive ? 'active' : ''}>Roster</NavLink>
      <NavLink to="/availability" onClick={() => setMobileOpen(false)} className={({ isActive }) => isActive ? 'active' : ''}>Availability</NavLink>
      <NavLink to="/open-shifts" onClick={() => setMobileOpen(false)} className={({ isActive }) => isActive ? 'active' : ''}>Open Shifts</NavLink>
      {isOwner ? (
        <>
          <NavLink to="/settings" onClick={() => setMobileOpen(false)} className={({ isActive }) => isActive ? 'active' : ''}>Settings</NavLink>
          <NavLink to="/audit" onClick={() => setMobileOpen(false)} className={({ isActive }) => isActive ? 'active' : ''}>Audit Log</NavLink>
        </>
      ) : (
        <NavLink to="/account" onClick={() => setMobileOpen(false)} className={({ isActive }) => isActive ? 'active' : ''}>Account</NavLink>
      )}
      <NavLink to="/help" onClick={() => setMobileOpen(false)} className={({ isActive }) => isActive ? 'active' : ''}>Help</NavLink>
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', margin: '8px 0' }} />
      <div style={{ padding: '4px 24px', color: '#94a3b8', fontSize: 13 }}>
        <strong style={{ color: '#fff' }}>{user?.name}</strong>
        {user?.role === 'owner' ? ' (Owner)' : ` (${user?.employeeRole || 'Staff'})`}
      </div>
      <button
        onClick={handleLogout}
        style={{ display: 'block', width: 'calc(100% - 48px)', margin: '8px 24px 0', background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', padding: '10px', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
      >
        Log out
      </button>
    </div>
    </>
  );
}
